import { getEdgeFunctionHeaders } from './edgeFunctions';
import { getSupabaseFunctionUrl } from './supabase';

const PROXY_DOWNLOAD_URL = getSupabaseFunctionUrl('proxy-download');

/**
 * Force-download a file from any URL (including cross-origin).
 * For video files, routes through a server-side proxy to bypass CORS restrictions.
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
  const isVideo = filename.endsWith('.mp4') || filename.endsWith('.webm') || filename.endsWith('.mov');

  if (isVideo) {
    // Route video downloads through our proxy edge function to bypass CORS
    try {
      const proxyUrl = `${PROXY_DOWNLOAD_URL}?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`;
      const response = await fetch(proxyUrl, {
        headers: await getEdgeFunctionHeaders(),
      });
      if (response.ok) {
        const blob = await response.blob();
        triggerBlobDownload(blob, filename);
        return;
      }
    } catch {
      // Proxy failed — fall back to opening in new tab
    }
    // Final fallback: open in new tab (user can right-click → Save Video As)
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  // For images and other files: try direct fetch first
  try {
    const response = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (response.ok) {
      const blob = await response.blob();
      triggerBlobDownload(blob, filename);
      return;
    }
  } catch {
    // CORS blocked — fall through
  }

  // Anchor download fallback
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Reliable blob-based download trigger. */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Keep the URL alive long enough for the browser to process the click
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/**
 * Convert a data-URL string to a Blob.
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/**
 * Fetch a remote URL and return it as a base64 data URL.
 * Tries several strategies to work around CORS restrictions.
 */
async function fetchAsDataUrl(src: string): Promise<string | null> {
  if (src.startsWith('data:')) return src;

  // Strategy 1: standard CORS fetch
  for (const mode of ['cors', 'no-cors'] as Array<'cors' | 'no-cors'>) {
    try {
      const resp = await fetch(src, { mode, cache: 'no-store' });
      if (!resp.ok) continue;
      const blob = await resp.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      if (dataUrl && dataUrl !== 'data:') return dataUrl;
    } catch {
      // try next strategy
    }
  }

  // Strategy 2: load via Image element with crossOrigin then draw to canvas
  try {
    const dataUrl = await new Promise<string | null>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // must be set BEFORE src
      img.onload = () => {
        try {
          const cvs = document.createElement('canvas');
          cvs.width = img.naturalWidth || 800;
          cvs.height = img.naturalHeight || 600;
          const ctx = cvs.getContext('2d');
          if (!ctx) { resolve(null); return; }
          ctx.drawImage(img, 0, 0);
          resolve(cvs.toDataURL('image/png'));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = src;
      setTimeout(() => resolve(null), 8000);
    });
    if (dataUrl) return dataUrl;
  } catch {
    // fall through
  }

  return null;
}

/**
 * Pre-load all <img> elements inside `node` as same-origin data URLs so that
 * html-to-image can draw them without tainted-canvas errors.
 * Returns a cleanup function that restores original srcs.
 */
async function preloadImagesAsBlobs(node: HTMLElement): Promise<() => void> {
  const imgs = Array.from(node.querySelectorAll('img')) as HTMLImageElement[];
  const restorations: Array<{ img: HTMLImageElement; original: string }> = [];

  for (const img of imgs) {
    const original = img.src;
    restorations.push({ img, original });

    if (!original || original.startsWith('data:')) continue;

    const dataUrl = await fetchAsDataUrl(original);
    if (!dataUrl) continue;

    // Replace src with data URL — always same-origin, no CORS taint
    img.removeAttribute('crossOrigin');
    img.src = dataUrl;

    // Wait for the browser to decode the new src
    await new Promise<void>((resolve) => {
      if (img.complete && img.naturalWidth > 0) { resolve(); return; }
      img.onload = () => resolve();
      img.onerror = () => resolve();
      setTimeout(resolve, 3000);
    });
  }

  return () => {
    restorations.forEach(({ img, original }) => {
      img.src = original;
    });
  };
}

/**
 * Capture a DOM node as a PNG and trigger a browser download.
 * Returns an error string if capture failed, or null on success.
 */
export async function captureNodeAsPng(
  node: HTMLElement,
  filename: string,
): Promise<string | null> {
  let cleanup: (() => void) | null = null;

  try {
    const { toPng } = await import('html-to-image');

    cleanup = await preloadImagesAsBlobs(node);

    let dataUrl: string;
    try {
      dataUrl = await toPng(node, {
        quality: 1,
        pixelRatio: 2,
        cacheBust: false,
        skipAutoScale: false,
        skipFonts: false,
        // Prevent html-to-image from re-fetching images we've already inlined
        fetchRequestInit: { mode: 'cors' as const, cache: 'no-store' as const },
      });
    } catch (innerErr) {
      // Second attempt: skip font embedding (sometimes the culprit on some browsers)
      try {
        dataUrl = await toPng(node, {
          quality: 1,
          pixelRatio: 2,
          cacheBust: false,
          skipFonts: true,
        });
      } catch {
        throw new Error(
          innerErr instanceof Error
            ? `Render failed: ${innerErr.message}`
            : 'Could not render the canvas. Please try again.',
        );
      }
    }

    if (!dataUrl || dataUrl === 'data:,') {
      throw new Error('Export produced an empty image. Please try again.');
    }

    // Convert data URL → Blob for a more reliable download (avoids popup blocker)
    const blob = dataUrlToBlob(dataUrl);
    triggerBlobDownload(blob, filename);
    return null; // success
  } catch (err) {
    return err instanceof Error ? err.message : 'Export failed. Please try again.';
  } finally {
    cleanup?.();
  }
}
